// THE PASTED ROSTER'S PURE CORE — text an operator pasted, in; the wire's
// `RosterElector[]` and a list of ADDRESSED problems, out. No DOM, no React, no
// fetch: `RollPanel.tsx` is a thin form over these answers, exactly as
// `BallotBuilder.tsx` is a thin form over `spec.ts` and the open/abandon panels are
// over `state.ts`.
//
// ── WHY THIS EXISTS AT ALL: THE WORKER REFUSES THE WHOLE ROSTER WITH ONE WORD ──
// `validateRoster` (`@cca/decides-worker/src/roll-load.ts`) returns the FIRST
// refusal it meets as a single word, names no row, and writes nothing. So a
// 300-line roster with one bad address comes back as `email_malformed` and the
// registrar has no idea which line. That is unusable at a desk and unthinkable
// under time pressure at a sitting. This module therefore finds the bad rows and
// says WHICH — every one of them at once, by line number, the same discipline
// `validateDraft` follows in `spec.ts` ("a form shows every blocked field at once
// rather than one per round-trip").
//
// ── IT REFUSES NOTHING THE SERVER DOES NOT (ARCH-2) ────────────────────────
// Every rule here is a MIRROR in front of the worker's own, never a second
// authority: the roster is re-validated server-side on the request that loads it,
// and a paste this module passed can still be refused. Nothing is decided here that
// the server does not decide again. What is bought is the ADDRESS — which line —
// which the wire has no room for.
//
// ── THE RULES ARE TRANSCRIBED, NOT IMPORTED, AND THAT IS A STATED LIMIT ────
// `web-must-not-import-worker` is enforced at error (ARCH-2), so this browser
// bundle cannot import the worker's `validateRoster`; the email shape below and the
// 500 ceiling are TRANSCRIBED from `src/roll-load.ts` (read 2026-09-01), the same
// arrangement — and the same limit — that `../api/client.ts` records for the wire
// types and `spec.ts` records for the ballot spec. **A rule that moves in the worker
// leaves this file silently agreeing with a server that no longer agrees with it.**
// The direction of that failure is safe (the server still refuses), but the operator
// then meets a one-word refusal with no line number, which is the state this module
// exists to end.
//
// ── IT IMPORTS ONLY TYPES ──────────────────────────────────────────────────
// Both imports are `import type`, fully erased at runtime, so this module has no
// runtime dependency on `../api/client` (which pulls `@cca/ui`) and a test of it is
// hermetic in the default node environment — the property `state.ts`, `spec.ts` and
// `../paper` all hold.
//
// ── PII: NO CELL AN OPERATOR PASTED IS QUOTED IN A MESSAGE ─────────────────
// A roster is real people on a real roll (Privacy Act, IPP), and these messages are
// read on screen and pasted into tickets. The rule is MECHANICAL rather than a
// judgement about which column a value came from: a message may quote a pasted cell
// only from inside a branch where an ANCHORED pattern has already proved the WHOLE
// value's characters. Today that is exactly one place — `contact_id_mangled`, whose
// {@link MANGLED_CONTACT_ID} admits digits, one dot and an exponent and so can hold
// no `@`, no space and no name (and is in any case an id the spreadsheet has already
// destroyed). Everything else is DESCRIBED, never repeated: the line, the column
// position, what a correct value looks like, and a deliberately coarse reading of
// what the cell holds ({@link describeCell}, {@link whyNotAnAddress}).
//
// The rule this file USED to state — "the one email ever quoted is one that FAILED
// the shape test" — was false twice over, and both failures are ordinary rather than
// exotic. (1) The House and May-vote cells were quoted verbatim and were never
// shape-tested at all, so a headerless paste one column out of line put a live
// address in EVERY line's message. (2) Failing the shape test does not make a value
// safe: `Name <name@example.org>`, the everyday address-book paste, fails it while
// carrying both a real address and a real name. Hence: test the VALUE before
// quoting it, never the field it came from.
//
// LIMIT, stated rather than denied: the textarea in `RollPanel.tsx` still holds the
// paste itself, because the operator is editing their own roster in front of them.
// What is bounded here is the DERIVED text — the problem list, which is the part
// that travels. Nothing here logs: there is no `console` call in this file and there
// must never be one.

import type { RegisterHouse, RosterElector } from '../api/client';

// ═══════════════════════════════════════════════════════════════════════════
//  THE WORKER'S OWN CONSTANTS, TRANSCRIBED
// ═══════════════════════════════════════════════════════════════════════════

/** `MAX_ELECTORS` (`src/roll-load.ts`) — a generous ceiling, not a policy on synod
 *  size. Exported so the panel names the same number the refusal does. */
export const MAX_ELECTORS = 500;

/** `EMAIL_SHAPE` (`src/roll-load.ts`), which is itself the schema's
 *  `contact_email_is_address_shaped` mirrored. A SHAPE test and not validation — the
 *  OTP the sign-in door sends is the real proof of an address. Character for
 *  character the worker's regex, so a paste this admits is one that route admits. */
const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** U+FEFF, the UTF-8 byte-order mark. Named by CODE POINT and never written as the
 *  character: a literal BOM in source is invisible to a reviewer, does not survive a
 *  copy-paste reliably, and is an ESLint `no-irregular-whitespace` error outside a
 *  string. Every CSV Excel writes begins with one. */
const BOM_CODE_POINT = 0xfeff;

/** The three literals `elector_house_is_known` admits, keyed by their canonical
 *  form so `Clergy`, `CLERGY` and ` clergy ` all land on the wire's `clergy`. */
const HOUSES: Record<string, RegisterHouse> = {
  clergy: 'clergy',
  laity: 'laity',
  bishop: 'bishop',
};

/**
 * A Contacts ID a spreadsheet has rounded into scientific notation.
 *
 * **This is the one refusal here with no counterpart in the worker, and it is the
 * most valuable check in the file.** A Zoho `Contacts.id` is a long run of digits;
 * open a CSV of them in Excel or Numbers and the column is read as a NUMBER, so
 * `4256000000123456789` is re-rendered `4.256E+18` and the true id is gone. The
 * worker cannot refuse that — it is a non-empty string, so it passes
 * `contact_id_required` — and the result is a whole roll of plausible, WRONG ids
 * that locks out every member at sign-in, discovered at the sitting. It requires a
 * decimal point AND an exponent, so no lawful id can match it.
 */
const MANGLED_CONTACT_ID = /^\d+\.\d+[eE][+-]?\d+$/;

/** The words the optional May-vote column admits, either way. A closed list on
 *  purpose: `mayVote` decides whether a person is in the majority base, so a word
 *  this module cannot read is REFUSED and shown, never guessed at. */
const YES_WORDS = new Set(['yes', 'y', 'true', 't', '1']);
const NO_WORDS = new Set(['no', 'n', 'false', 'f', '0']);

// ═══════════════════════════════════════════════════════════════════════════
//  WHAT THIS MODULE ANSWERS
// ═══════════════════════════════════════════════════════════════════════════

export type PasteProblemCode =
  | 'no_electors'
  | 'too_many_electors'
  /** Not a wire word — the first line names columns and one this module needs is
   *  not among them. Reported ONCE for the paste rather than as a problem on every
   *  line, which is the difference between one sentence and three hundred. */
  | 'header_columns_missing';

export type LineProblemCode =
  | 'contact_id_required'
  | 'duplicate_contact'
  | 'email_required'
  | 'email_malformed'
  | 'ambiguous_identity'
  | 'house_unknown'
  | 'vote_flag_invalid'
  /** Not a wire word — the line has too few columns to read at all. Reported INSTEAD
   *  of the three field refusals it would otherwise cascade into, so a paste of one
   *  column reads as 300 problems rather than 900. */
  | 'too_few_columns'
  /** Not a wire word — see {@link MANGLED_CONTACT_ID}. */
  | 'contact_id_mangled';

/**
 * Why one line, or the whole paste, cannot go on the roll. **BLOCKING**: the panel
 * does not offer the load while any of these stands.
 *
 * `code` is the WORKER's word wherever the worker has one, so a check can name the
 * refusal it mirrors without matching prose; the three that have none are marked as
 * such on the unions above.
 *
 * `message` deliberately does NOT repeat the line number — the caller composes
 * "Line 12 — …" from `line`, so a problem cannot render its address twice or, worse,
 * render one that disagrees with the field a test reads.
 */
export type RosterProblem =
  | { at: 'paste'; code: PasteProblemCode; message: string }
  | { at: 'line'; line: number; code: LineProblemCode; message: string };

/**
 * A roster that will LOAD and cannot be FROZEN. Kept apart from
 * {@link RosterProblem} because it does not withhold the load: the roll is
 * rebuildable working state right up to the freeze, and a screen that refused a
 * lawful load would be over-strict. But the operator is told BEFORE they commit,
 * because each of these is a `roll/freeze` refusal (`RollFreezeError`) waiting at
 * the one act that cannot be undone.
 */
export interface RosterWarning {
  code: 'clergy_house_empty' | 'laity_house_empty' | 'too_many_bishops';
  message: string;
}

/** What {@link parseRoster} answers. */
export interface RosterParse {
  /**
   * The rows that parsed cleanly, ready for `POST …/roll`. A row carrying ANY
   * problem contributes no elector, so with problems outstanding this is what WOULD
   * load once they are fixed — which is why the panel gates the load on
   * `problems.length === 0` rather than on this being non-empty.
   */
  electors: RosterElector[];
  /** EVERY problem, never the first. See {@link RosterProblem}. */
  problems: RosterProblem[];
  /** Only computed when `problems` is empty — a House count taken over a partial
   *  read would warn about a House the unread lines hold. */
  warnings: RosterWarning[];
  /** How many DATA records were read (a header row and blank lines excluded), so the
   *  panel can say "3 of 210 lines have a problem" rather than only the good count.
   *  One record can carry more than one problem, so this is not derivable from the
   *  two lists above. */
  rowsRead: number;
}

// ═══════════════════════════════════════════════════════════════════════════
//  THE SCANNER — text to records, RFC 4180 as far as it goes.
// ═══════════════════════════════════════════════════════════════════════════

/** One record as it came off the scanner, with the 1-based PHYSICAL line it starts
 *  on. The line counts every line in the box — the header row and blank lines
 *  included — so it is the line an operator can count to, or scroll to in the
 *  spreadsheet the text came from. A record carrying a quoted newline spans more
 *  than one physical line and is named by its FIRST. */
interface RawRecord {
  line: number;
  cells: string[];
}

/**
 * Split the pasted text into records and fields.
 *
 * Handles what a real paste carries: a UTF-8 BOM (stripped by the caller), CRLF or
 * LF, a trailing newline, blank lines, quoted fields containing the delimiter, and
 * quoted fields containing a NEWLINE — the last is why this scans the whole text
 * character by character rather than splitting on lines first, which is the
 * estate's other CSV reader's shape (`cca-xero-web`'s `parseCsvLine`) and cannot
 * survive an address column in a Zoho export.
 *
 * `""` inside a quoted field is one literal quote (RFC 4180). A quote appearing
 * mid-field in UNQUOTED text opens quoting anyway rather than being kept as a
 * character: strictly that is not RFC 4180, and it is the forgiving direction —
 * the alternative refuses a field a spreadsheet emitted.
 */
function scan(text: string, delimiter: string): RawRecord[] {
  const records: RawRecord[] = [];
  let cells: string[] = [];
  let field = '';
  let inQuotes = false;
  let line = 1;
  let recordLine = 1;
  let i = 0;

  // `charAt` rather than `[i]` throughout: it is typed `string` and answers '' past
  // the end, so the lookahead needs no bounds guard under noUncheckedIndexedAccess.
  while (i < text.length) {
    const ch = text.charAt(i);

    if (inQuotes) {
      if (ch === '"') {
        if (text.charAt(i + 1) === '"') {
          field += '"';
          i += 2;
        } else {
          inQuotes = false;
          i += 1;
        }
        continue;
      }
      if (ch === '\r' || ch === '\n') {
        // A newline INSIDE a quoted field is content, normalised to LF. The physical
        // line still advances, so the NEXT record's reported line stays true.
        field += '\n';
        i += ch === '\r' && text.charAt(i + 1) === '\n' ? 2 : 1;
        line += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === delimiter) {
      cells.push(field);
      field = '';
      i += 1;
      continue;
    }
    if (ch === '\r' || ch === '\n') {
      cells.push(field);
      records.push({ line: recordLine, cells });
      cells = [];
      field = '';
      i += ch === '\r' && text.charAt(i + 1) === '\n' ? 2 : 1;
      line += 1;
      recordLine = line;
      continue;
    }
    field += ch;
    i += 1;
  }

  // The last record, when the text did not end with a newline. A text that DID end
  // with one leaves both empty here, so a trailing newline adds no blank record.
  if (field !== '' || cells.length > 0) {
    cells.push(field);
    records.push({ line: recordLine, cells });
  }
  return records;
}

/**
 * Comma or TAB.
 *
 * **The tab case is not exotic — it is the likeliest paste in the building.** A
 * registrar exports Zoho Contacts to CSV, opens it in Excel or Google Sheets to
 * tidy it, selects the columns and copies: the clipboard then holds TAB-separated
 * text, and a comma-only reader sees one column per line and refuses all 300 of
 * them. Decided on the first non-blank line so a comma inside a later quoted field
 * cannot swing it. A semicolon-separated export (a European locale) is NOT detected
 * — it reads as one column and earns `too_few_columns` on every line, which is
 * legible but unhelpful; it is out of scope rather than overlooked.
 */
function detectDelimiter(text: string): string {
  const firstLine = text.split(/\r?\n/).find((candidate) => candidate.trim() !== '') ?? '';
  const tabs = (firstLine.match(/\t/g) ?? []).length;
  const commas = (firstLine.match(/,/g) ?? []).length;
  return tabs > commas ? '\t' : ',';
}

// ═══════════════════════════════════════════════════════════════════════════
//  THE COLUMNS — named by a header row, or in a fixed order without one.
// ═══════════════════════════════════════════════════════════════════════════

/** A header cell reduced to letters and digits, so `Contact ID`, `contact_id` and
 *  `CONTACTID` are one name. */
function canon(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * The header names each column answers to, MOST SPECIFIC FIRST — {@link indexOf}
 * returns the first alias that matches any cell, so a sheet carrying both
 * `Contact Id` and a generic `Id` binds to the former. Deliberately short lists: a
 * generous matcher that bound the wrong column would seat people under somebody
 * else's identity, and a name this module does not know is a VISIBLE refusal rather
 * than a wrong guess.
 */
const COLUMN_ALIASES = {
  contactId: ['contactid', 'zohocontactid', 'zohorecordid', 'zohoid', 'recordid', 'id'],
  email: ['email', 'emailaddress', 'primaryemail'],
  house: ['house'],
  mayVote: ['mayvote', 'canvote', 'voting', 'vote'],
} as const;

type ColumnName = keyof typeof COLUMN_ALIASES;

/** Where each column sits, `-1` for absent. Under a header these are read from the
 *  names; without one they are the FIXED ORDER documented on {@link parseRoster}. */
type ColumnIndex = Record<ColumnName, number>;

const FIXED_ORDER: ColumnIndex = { contactId: 0, email: 1, house: 2, mayVote: 3 };

/** The three columns a roster cannot be read without. `mayVote` is genuinely
 *  optional — absent means everyone on the roster votes. */
const REQUIRED_COLUMNS = ['contactId', 'email', 'house'] as const;

function indexOf(header: readonly string[], aliases: readonly string[]): number {
  for (const alias of aliases) {
    const found = header.findIndex((cell) => canon(cell) === alias);
    if (found >= 0) return found;
  }
  return -1;
}

/** The operator-facing name for a column this module could not find. */
const COLUMN_LABEL: Record<ColumnName, string> = {
  contactId: 'Contacts ID',
  email: 'Email',
  house: 'House',
  mayVote: 'May vote',
};

// ═══════════════════════════════════════════════════════════════════════════
//  SAYING WHAT IS WRONG WITHOUT SAYING WHAT IS THERE
// ═══════════════════════════════════════════════════════════════════════════
// The whole of this module's vocabulary for talking about a cell it will not
// repeat. See the PII note at the head of the file for why it exists; between them
// these three replace what a bad line used to say about itself.

/**
 * "column 3" — the 1-based position of a column in the pasted line, which is where
 * the operator looks once the value itself is not shown. It is true with or without
 * a header row: under a header the order is free, so the POSITION is the part the
 * operator cannot work out for themselves.
 *
 * No `-1` reaches this. The three required columns are proved present before any
 * line is read (`header_columns_missing` returns first, and without a header they
 * are {@link FIXED_ORDER}), and `mayVote` is spoken of only where it held a value,
 * which an absent column cannot.
 */
function columnAt(index: number): string {
  return `column ${String(index + 1)}`;
}

/**
 * What a cell holds, in this module's own words rather than the operator's.
 *
 * COARSE ON PURPOSE — a description fine enough to reconstruct the value IS the
 * value, so the answers are five fixed literals and none of them varies with the
 * content beyond the branch taken. The one discrimination that earns its place is
 * the `@`: a House or a May-vote cell holding an address is the signature of a paste
 * whose columns are one out of line, which is the commonest way a good export goes
 * wrong and is a diagnosis "that is not a House" cannot give. The character is
 * TESTED, never echoed.
 *
 * `'nothing'` is defensive: both callers today handle the empty cell in their own
 * branch, because an empty cell wants different advice from a wrong one.
 */
function describeCell(value: string): string {
  if (value === '') return 'nothing';
  if (value.includes('@')) return 'an email address';
  if (/\s/.test(value)) return 'more than one word';
  if (/^[0-9]+$/.test(value)) return 'digits';
  return 'one word';
}

/** The sentence that follows an address found where an address does not belong —
 *  appended to the House and May-vote refusals, which are the two columns a
 *  one-place shift fills with addresses. Empty for every other value, so an ordinary
 *  typo is not lectured about column order. */
function shiftedColumnsHint(value: string): string {
  return value.includes('@')
    ? ' An address in this column usually means the line’s columns are one out of order: with no first line naming them, the order is Contacts ID, email, House, May vote.'
    : '';
}

/**
 * WHY a cell failed {@link EMAIL_SHAPE}, said without repeating any of it.
 *
 * The message this replaces quoted the cell, on the reasoning that a value failing
 * the SHAPE test is not a deliverable address. Neither the near miss nor the far one
 * is safe. `Jack Smith <jack@example.org>` — the ordinary Outlook or address-book
 * paste — fails the shape test while carrying a real address AND a real name. And
 * `bob@@example`, the "merely a typo" case, carries a local part that is routinely a
 * person's own name and is one deleted character from a live mailbox: a typo is a
 * mangled address, not a different kind of thing. So NOTHING from this cell is
 * echoed. The operator loses least by that: they are looking at the value in the box
 * as they read this, and what they did not have is WHICH line and WHAT is wrong.
 *
 * TOTAL: every arm returns a whole sentence and the last answers for anything the
 * others do not name. Given the arms above it is unreachable against today's shape —
 * it is kept because that shape is TRANSCRIBED from the worker (see the head of this
 * file) and can drift, and a drifted rule must still produce a true sentence.
 */
function whyNotAnAddress(value: string): string {
  const ats = (value.match(/@/g) ?? []).length;
  if (ats === 0) {
    return 'There is no @ in it. If that cell holds a name or a House, the line’s columns are one out of order: with no first line naming them, the order is Contacts ID, email, House, May vote.';
  }
  if (/\s/.test(value)) {
    return 'It has a space in it. If it was copied as a name with the address in angle brackets after it, paste just the address.';
  }
  if (ats > 1) return 'It has more than one @ in it.';
  const at = value.indexOf('@');
  if (at === 0) return 'There is nothing before the @.';
  const domain = value.slice(at + 1);
  if (domain === '') return 'There is nothing after the @.';
  if (!/^[^@\s]+\.[^@\s]+$/.test(domain)) {
    return 'The part after the @ is not a domain name: it needs a dot with something on each side, as in “example.org”.';
  }
  return 'It is not the shape of an email address.';
}

// ═══════════════════════════════════════════════════════════════════════════
//  THE PARSE
// ═══════════════════════════════════════════════════════════════════════════

/** One cell, or '' where the record is short or the column is absent. */
function cellAt(cells: readonly string[], index: number): string {
  return index < 0 ? '' : (cells[index] ?? '');
}

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

/** "House", "Email and House", "Contacts ID, Email and House" — no Oxford comma,
 *  because these are read aloud inside a sentence. */
function listWords(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1] ?? ''}`;
}

/**
 * PARSE A PASTED ROSTER.
 *
 * ── WHAT IT ACCEPTS ────────────────────────────────────────────────────────
 * Comma- or TAB-separated text, one elector per line, with or without a header row:
 *
 *  · **With a header row** — the first line is treated as a header the moment ANY of
 *    its cells names a column this module knows ({@link COLUMN_ALIASES}); column
 *    order is then free and every unrecognised column (a full Zoho export carries
 *    dozens) is ignored. A header missing `Contacts ID`, `Email` or `House` is ONE
 *    refusal for the paste, not one per line.
 *  · **Without a header row** — the order is FIXED and is
 *    `Contacts ID, email, House[, May vote]`. Columns beyond the fourth are ignored.
 *
 * Tolerated throughout: a UTF-8 BOM, CRLF or LF, a trailing newline, blank lines
 * anywhere, quoted fields containing the delimiter or a newline, and surrounding
 * whitespace on every value.
 *
 * ── WHAT IT NORMALISES, AND WHAT IT REFUSES ────────────────────────────────
 * It repairs only what is unambiguous, which is whitespace and case: the email is
 * trimmed and lowercased (so the worker's `email_not_normalised` is UNREACHABLE from
 * this surface), the Contacts ID is trimmed (an untrimmed one is WRITTEN with its
 * spaces and then never matches at sign-in), and the House word is trimmed and
 * lowercased. Everything else is REFUSED, never guessed at: a malformed address, an
 * unknown House, a missing ID, an unreadable May-vote word and an ID a spreadsheet
 * has mangled are each reported against their LINE and their COLUMN — and, apart
 * from the mangled ID, without the cell being repeated (see the PII note above).
 *
 * ── IT IS TOTAL ────────────────────────────────────────────────────────────
 * Every input answers; nothing throws. Empty text answers `no_electors` — the panel,
 * not this module, decides that an untouched box shows no problems at all.
 */
export function parseRoster(text: string): RosterParse {
  const problems: RosterProblem[] = [];
  // The BOM would otherwise become part of the first header cell — or, with no
  // header, of the first Contacts ID, which then matches nothing in Zoho.
  const body = text.codePointAt(0) === BOM_CODE_POINT ? text.slice(1) : text;
  const records = scan(body, detectDelimiter(body)).filter((record) =>
    record.cells.some((cell) => cell.trim() !== ''),
  );

  // ── The header, if there is one ─────────────────────────────────────────
  const first = records[0];
  const headerCells = first === undefined ? [] : first.cells;
  const named: ColumnIndex = {
    contactId: indexOf(headerCells, COLUMN_ALIASES.contactId),
    email: indexOf(headerCells, COLUMN_ALIASES.email),
    house: indexOf(headerCells, COLUMN_ALIASES.house),
    mayVote: indexOf(headerCells, COLUMN_ALIASES.mayVote),
  };
  const hasHeader = Object.values(named).some((index) => index >= 0);
  const column = hasHeader ? named : FIXED_ORDER;
  const dataRecords = hasHeader ? records.slice(1) : records;

  if (hasHeader) {
    const missing = REQUIRED_COLUMNS.filter((name) => column[name] < 0);
    if (missing.length > 0) {
      const names = listWords(missing.map((name) => COLUMN_LABEL[name]));
      problems.push({
        at: 'paste',
        code: 'header_columns_missing',
        message: `The first line names columns, but not ${names}. Name ${plural(missing.length, 'that column', 'those columns')} — or remove the first line and put the columns in this order: Contacts ID, email, House, May vote.`,
      });
      return { electors: [], problems, warnings: [], rowsRead: 0 };
    }
  }

  // ── The two whole-paste refusals ────────────────────────────────────────
  if (dataRecords.length === 0) {
    problems.push({
      at: 'paste',
      code: 'no_electors',
      message:
        'Nothing was read from that. Put one elector on each line: their Contacts ID, their email address and their House.',
    });
    return { electors: [], problems, warnings: [], rowsRead: 0 };
  }
  if (dataRecords.length > MAX_ELECTORS) {
    // Refused BEFORE the row checks, so an oversized paste reads as one sentence
    // rather than as several hundred problems with the real fault buried in them.
    problems.push({
      at: 'paste',
      code: 'too_many_electors',
      message: `That is ${String(dataRecords.length)} electors, and one load takes at most ${String(MAX_ELECTORS)}. Nothing has been checked line by line. If a synod is genuinely larger than that, tell the registrar.`,
    });
    return { electors: [], problems, warnings: [], rowsRead: dataRecords.length };
  }

  // The lowest cell count a record needs for the three required columns to exist.
  const minCells = 1 + Math.max(column.contactId, column.email, column.house);

  const electors: RosterElector[] = [];
  /** Contacts ID -> the line that first claimed it (`duplicate_contact`). */
  const contactLine = new Map<string, number>();
  /** Address -> the id and line that first claimed it. A second, DIFFERENT id for
   *  one address is the shared parish mailbox `resolveContactId` refuses — it locks
   *  BOTH holders out at sign-in, so it is caught here rather than at the door. */
  const emailOwner = new Map<string, { contactId: string; line: number }>();

  for (const record of dataRecords) {
    const { line } = record;
    const before = problems.length;

    if (record.cells.length < minCells) {
      // INSTEAD of the field refusals, not as well as: see `too_few_columns`.
      const count = record.cells.length;
      problems.push({
        at: 'line',
        line,
        code: 'too_few_columns',
        message: `This line has ${String(count)} ${plural(count, 'column', 'columns')}. A roster line needs the Contacts ID, the email address and the House.`,
      });
      continue;
    }

    // ── The Contacts ID — this platform's identity, never the address (ADR-059) ──
    const contactId = cellAt(record.cells, column.contactId).trim();
    if (contactId === '') {
      problems.push({
        at: 'line',
        line,
        code: 'contact_id_required',
        message: `No Contacts ID in ${columnAt(column.contactId)}. This platform seats people by ID and never by name, so every line needs one.`,
      });
    } else if (MANGLED_CONTACT_ID.test(contactId)) {
      // The ONE pasted cell this module quotes, and the exception is mechanical, not
      // a judgement about the column: `MANGLED_CONTACT_ID` is anchored over the whole
      // value and admits only digits, one dot and an exponent, so what is shown here
      // can hold no `@`, no space and no name. Quoting it is also what makes the
      // problem legible — "scientific notation" is abstract until you see `4.26E+18`.
      // See the PII note at the head of this file.
      problems.push({
        at: 'line',
        line,
        code: 'contact_id_mangled',
        message: `The Contacts ID in ${columnAt(column.contactId)} reads “${contactId}” — a spreadsheet has rounded it into scientific notation, and the real ID cannot be recovered from it. Format that column as text in the spreadsheet and copy it again.`,
      });
    } else {
      // Registered even when the line has OTHER problems: a repeat of a bad line's
      // id is still a repeat, and the operator should be told both facts at once.
      const prior = contactLine.get(contactId);
      if (prior === undefined) {
        contactLine.set(contactId, line);
      } else {
        problems.push({
          at: 'line',
          line,
          code: 'duplicate_contact',
          message: `The same Contacts ID is already on line ${String(prior)}, and each person is on the roll once.`,
        });
      }
    }

    // ── The address — normalised here, so `email_not_normalised` cannot arise ──
    const rawEmail = cellAt(record.cells, column.email).trim();
    const email = rawEmail.toLowerCase();
    let emailUsable = false;
    if (email === '') {
      problems.push({
        at: 'line',
        line,
        code: 'email_required',
        message: `No email address in ${columnAt(column.email)}, and a member signs in with theirs.`,
      });
    } else if (!EMAIL_SHAPE.test(email)) {
      // The cell is NOT repeated: failing the shape test does not make a value safe.
      // `rawEmail` is the value under the operator's eye in the box, so what the
      // message owes them is the line, the column and the fault. See
      // {@link whyNotAnAddress} and the PII note at the head of this file.
      problems.push({
        at: 'line',
        line,
        code: 'email_malformed',
        message: `The email address in ${columnAt(column.email)} cannot be read. ${whyNotAnAddress(rawEmail)}`,
      });
    } else {
      emailUsable = true;
    }

    if (emailUsable && contactId !== '') {
      const owner = emailOwner.get(email);
      if (owner === undefined) {
        emailOwner.set(email, { contactId, line });
      } else if (owner.contactId !== contactId) {
        problems.push({
          at: 'line',
          line,
          code: 'ambiguous_identity',
          message: `This address is already on line ${String(owner.line)} against a DIFFERENT Contacts ID. One address shared by two people locks BOTH of them out at sign-in — give each their own address.`,
        });
      }
    }

    // ── The House ──────────────────────────────────────────────────────────
    const rawHouse = cellAt(record.cells, column.house).trim();
    const house = HOUSES[rawHouse.toLowerCase()];
    if (house === undefined) {
      // `rawHouse` is NOT quoted. This cell is not shape-tested by anything, so a
      // paste one column out of line puts a live address here on every row — which
      // is the very case the message now names instead of echoing.
      problems.push({
        at: 'line',
        line,
        code: 'house_unknown',
        message:
          rawHouse === ''
            ? `No House in ${columnAt(column.house)}. Everyone on the roll is clergy, laity or bishop.`
            : `The House in ${columnAt(column.house)} is not clergy, laity or bishop — that cell holds ${describeCell(rawHouse)}.${shiftedColumnsHint(rawHouse)}`,
      });
    }

    // ── May vote — blank is TRUE, `no` marks an Officer ───────────────────
    const rawVote = cellAt(record.cells, column.mayVote).trim();
    let mayVote: boolean | null = null;
    if (rawVote !== '') {
      const word = rawVote.toLowerCase();
      if (YES_WORDS.has(word)) mayVote = true;
      else if (NO_WORDS.has(word)) mayVote = false;
      else {
        // `rawVote` is NOT quoted, for the same reason as the House above: nothing
        // shape-tests this cell, so a shifted paste fills it with real addresses.
        problems.push({
          at: 'line',
          line,
          code: 'vote_flag_invalid',
          message: `The May vote in ${columnAt(column.mayVote)} is not yes or no — that cell holds ${describeCell(rawVote)}. Leave it empty for an ordinary member, or put “no” for an Officer, who stays on the roll and in their House but outside the vote.${shiftedColumnsHint(rawVote)}`,
        });
      }
    }

    // A line carrying ANY problem contributes no elector. `house === undefined` is
    // tested as well as the count because it is what NARROWS `house` for the push
    // below — a length comparison tells the compiler nothing.
    if (house === undefined || problems.length !== before) continue;

    // `mayVote` is OMITTED rather than sent `undefined`: the field is optional under
    // exactOptionalPropertyTypes, and the worker's default for an absent one is
    // TRUE. An explicit `yes` is dropped for the same reason, so the body carries the
    // flag only where it says something — an Officer.
    electors.push(
      mayVote === false ? { contactId, email, house, mayVote: false } : { contactId, email, house },
    );
  }

  return {
    electors,
    problems,
    warnings: problems.length === 0 ? freezeWarnings(electors) : [],
    rowsRead: dataRecords.length,
  };
}

/**
 * The three `roll/freeze` refusals a roster can be seen to carry BEFORE it is
 * loaded, so the operator meets them here rather than at the one act that cannot be
 * undone. Counted over the majority BASE — the electors who may vote — because that
 * is what `freezeRoll` counts: an Officer is on the roll and in a House and is not
 * in the base, so a House of Officers alone is still `clergy_house_empty`.
 */
function freezeWarnings(electors: readonly RosterElector[]): RosterWarning[] {
  const warnings: RosterWarning[] = [];
  // `mayVote !== false` is the default-TRUE rule, not a slip: an absent flag means
  // the person votes.
  const base = electors.filter((elector) => elector.mayVote !== false);
  const inHouse = (house: RegisterHouse): number =>
    base.filter((elector) => elector.house === house).length;

  if (inHouse('clergy') === 0) {
    warnings.push({
      code: 'clergy_house_empty',
      message:
        'No Clergy who may vote. This roster will load, but the roll cannot be FROZEN with a House empty — and a ballot is opened against a frozen roll.',
    });
  }
  if (inHouse('laity') === 0) {
    warnings.push({
      code: 'laity_house_empty',
      message:
        'No Laity who may vote. This roster will load, but the roll cannot be FROZEN with a House empty — and a ballot is opened against a frozen roll.',
    });
  }
  if (inHouse('bishop') > 1) {
    warnings.push({
      code: 'too_many_bishops',
      message:
        'More than one Bishop. A diocese has one Bishop or none, and the roll cannot be FROZEN while the roster names two.',
    });
  }
  return warnings;
}
